// src/pages/sqf/SqfScheduleOverviewPage.js
//
// Global schedule overview — one row per active schedule across all templates.
// Shows: template, label, frequency, start date, assigned user, last completed, next due.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const FREQUENCY_LABELS = {
  daily:     'Daily',
  per_shift: 'Per Shift',
  weekly:    'Weekly',
  monthly:   'Monthly',
  on_demand: 'On-Demand',
};

const FREQ_BADGE = {
  daily:     'bg-primary',
  per_shift: 'bg-info text-dark',
  weekly:    'bg-success',
  monthly:   'bg-warning text-dark',
  on_demand: 'bg-secondary',
};

function displayName(u) {
  if (!u) return null;
  return (u.firstName || u.lastName)
    ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
    : u.username;
}

function NextDueCell({ nextDue, startDate, lang }) {
  if (!nextDue) return <span className="text-muted">—</span>;
  const today = new Date().toISOString().split('T')[0];
  const isNotStarted = startDate && startDate > today;
  if (isNotStarted) {
    return (
      <span className="text-muted" style={{ fontSize: 12 }}>
        {t('Starts', 'Inicia', lang)} {startDate}
      </span>
    );
  }
  const d = new Date(nextDue + 'T00:00:00');
  const diff = Math.round((d - new Date(today + 'T00:00:00')) / 86400000);
  let badge = 'bg-secondary';
  let label = nextDue;
  if (diff === 0) { badge = 'bg-warning text-dark'; label = t('Today', 'Hoy', lang); }
  else if (diff === 1) { badge = 'bg-primary'; label = t('Tomorrow', 'Mañana', lang); }
  else if (diff <= 7) { badge = 'bg-info text-dark'; label = t(`In ${diff} days`, `En ${diff} días`, lang); }
  return (
    <span>
      <span className={`badge ${badge} me-1`}>{label}</span>
      {diff > 1 && <span className="text-muted" style={{ fontSize: 11 }}>{nextDue}</span>}
    </span>
  );
}

function LastCompletedCell({ date, status, lang }) {
  if (!date) return <span className="text-muted" style={{ fontSize: 12 }}>{t('Never', 'Nunca', lang)}</span>;
  const color = status === 'qa_approved' ? 'text-success' : 'text-info';
  const label = status === 'qa_approved' ? t('Approved', 'Aprobado', lang) : t('Submitted', 'Enviado', lang);
  return (
    <span style={{ fontSize: 12 }}>
      {date} <span className={`${color} fw-semibold`} style={{ fontSize: 11 }}>· {label}</span>
    </span>
  );
}

export default function SqfScheduleOverviewPage() {
  const { lang } = useLanguage();
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    sqfService.getScheduleOverview()
      .then(data => setOverview(data))
      .catch(() => setError(t('Failed to load schedule overview.', 'Error al cargar el resumen de horarios.', lang)))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const frequencyLabel = (freq) => {
    const map = {
      daily:     t('Daily',     'Diario',      lang),
      per_shift: t('Per Shift', 'Por turno',   lang),
      weekly:    t('Weekly',    'Semanal',      lang),
      monthly:   t('Monthly',   'Mensual',      lang),
      on_demand: t('On-Demand', 'Bajo demanda', lang),
    };
    return map[freq] || freq;
  };

  const filtered = overview.filter(s => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      s.templateName?.toLowerCase().includes(q) ||
      s.label?.toLowerCase().includes(q) ||
      displayName(s.assignedUser)?.toLowerCase().includes(q) ||
      FREQUENCY_LABELS[s.frequency]?.toLowerCase().includes(q)
    );
  });

  if (loading) return (
    <div className="container mt-4 text-center py-5 text-muted">
      <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
    </div>
  );

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf" className="text-muted" style={{ fontSize: 12 }}>← {t('SQF Dashboard', 'Panel SQF', lang)}</Link>
          <h4 className="mb-0 mt-1">{t('Schedule Overview', 'Resumen de horarios', lang)}</h4>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            {t('All active schedules across all checklists — last completed and next due date.', 'Todos los horarios activos de todas las listas — última completada y próxima fecha de vencimiento.', lang)}
          </p>
        </div>
        <Link to="/sqf/templates" className="btn btn-outline-secondary btn-sm">{t('Manage Templates', 'Gestionar plantillas', lang)}</Link>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {overview.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p className="mb-3">{t('No active schedules yet.', 'Aún no hay horarios activos.', lang)}</p>
          <p style={{ fontSize: 13 }}>
            {t('Go to', 'Ir a', lang)} <Link to="/sqf/templates">{t('Templates', 'Plantillas', lang)}</Link>{t(', open a template, and click', ', abrir una plantilla y hacer clic en', lang)} <strong>{t('Schedule', 'Horario', lang)}</strong> {t('to set one up.', 'para configurar uno.', lang)}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3" style={{ maxWidth: 320 }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t('Filter by name, frequency, assignee…', 'Filtrar por nombre, frecuencia, asignado…', lang)}
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>{t('Checklist', 'Lista de verificación', lang)}</th>
                  <th>{t('Label', 'Etiqueta', lang)}</th>
                  <th>{t('Frequency', 'Frecuencia', lang)}</th>
                  <th>{t('Start Date', 'Fecha de inicio', lang)}</th>
                  <th>{t('Assigned To', 'Asignado a', lang)}</th>
                  <th>{t('Last Completed', 'Última completada', lang)}</th>
                  <th>{t('Next Due', 'Próximo vencimiento', lang)}</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id}>
                    <td>
                      <Link
                        to={`/sqf/templates/${s.templateId}/schedule`}
                        className="fw-semibold text-decoration-none"
                        style={{ fontSize: 13 }}
                      >
                        {s.templateName}
                      </Link>
                    </td>
                    <td style={{ fontSize: 12 }}>{s.label || <span className="text-muted">—</span>}</td>
                    <td>
                      <span className={`badge ${FREQ_BADGE[s.frequency] || 'bg-secondary'}`} style={{ fontSize: 11 }}>
                        {frequencyLabel(s.frequency)}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {s.startDate || <span className="text-muted">{t('Immediate', 'Inmediato', lang)}</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {s.assignedUser
                        ? displayName(s.assignedUser)
                        : <span className="text-muted">{t('Unassigned', 'Sin asignar', lang)}</span>
                      }
                    </td>
                    <td>
                      <LastCompletedCell date={s.lastCompleted} status={s.lastCompletedStatus} lang={lang} />
                    </td>
                    <td>
                      <NextDueCell nextDue={s.nextDue} startDate={s.startDate} lang={lang} />
                    </td>
                    <td className="text-end">
                      <Link
                        to={`/sqf/templates/${s.templateId}/schedule`}
                        className="btn btn-outline-secondary btn-sm"
                        style={{ fontSize: 11 }}
                      >
                        {t('Edit', 'Editar', lang)}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            {filtered.length} {t('of', 'de', lang)} {overview.length} {overview.length !== 1 ? t('schedules', 'horarios', lang) : t('schedule', 'horario', lang)}
          </div>
        </>
      )}
    </div>
    </>
  );
}
